/* custom-ring-configurator-pdp.js
 * - Moves configurator into #rc-pdp-mount-<sectionId> (preferred) or #rc-pdp-mount (legacy)
 * - Applies purity rules
 * - Syncs selected UI
 * - Re-tints Purity swatches based on selected Metal
 * - Redirects to matching product
 * - Accordion wrapper (open on desktop, closed on mobile)
 * - Loading state class while looking up match (sync flush, no awaits)
 */

(function () {
  const COLLECTION_GRID_SECTION_ID = 'main-collection-product-grid';

  // ACCORDION
  const ACC_BREAKPOINT = 750; // px

  function safeJsonParse(str, fallback) {
    try {
      if (!str || typeof str !== 'string') return fallback;
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  function debounce(fn, ms) {
    let t = null;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms || 0);
    };
  }

  function getSectionConfig(sectionId) {
    if (!window.ringConfiguratorPdp) return null;
    return window.ringConfiguratorPdp[sectionId] || null;
  }

  function getSelectedValue(formEl, filterName) {
    const input = formEl.querySelector(`input[name="${CSS.escape(filterName)}"]:checked`);
    return input ? input.value : '';
  }

  function setResult(root, html) {
    const el = root.querySelector('[data-rc-result]');
    if (el) el.innerHTML = html || '';
  }

  function setLoading(root, isLoading) {
    if (!root) return;
    root.classList.toggle('is-loading', !!isLoading);

    // Force a synchronous style/layout flush so the loading class "takes"
    // without introducing async waits that can interfere with navigation.
    // (Reading offsetHeight is the common flush trick.)
    // eslint-disable-next-line no-unused-expressions
    root.offsetHeight;
  }

  function buildFilterQueryString(mapObj, selections) {
    const parts = [];
    Object.keys(selections).forEach((filterLabel) => {
      const val = selections[filterLabel];
      if (!val) return;
      const key = mapObj[filterLabel];
      if (!key) return;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
    });
    return parts.join('&');
  }

  function getStepEl(root, filterName) {
    return root.querySelector(`[data-rc-step][data-filter-name="${CSS.escape(filterName)}"]`);
  }

  function syncSelectedClasses(stepEl) {
    if (!stepEl) return;

    const hasChecked = !!stepEl.querySelector('input[type="radio"]:checked');

    stepEl.querySelectorAll('[data-rc-option]').forEach((opt) => {
      const input = opt.querySelector('input[type="radio"]');
      const pill = opt.querySelector('.rc-pill');
      const checked = !!(input && input.checked);

      if (pill) pill.classList.toggle('selected', checked);

      const isDisabled = opt.classList.contains('is-disabled') || !!(input && input.disabled);
      opt.classList.toggle('is-unselected', hasChecked && !checked && !isDisabled);
    });
  }

  function syncAllSelectedClasses(root) {
    root.querySelectorAll('[data-rc-step]').forEach((step) => syncSelectedClasses(step));
  }

  function firstEnabledRadio(stepEl) {
    if (!stepEl) return null;
    const radios = stepEl.querySelectorAll('input[type="radio"]');
    for (const r of radios) if (!r.disabled) return r;
    return null;
  }

  function applyPuritySwatchesByMetal(root, cfg) {
    const form = root.querySelector('form');
    if (!form) return;

    const metalRaw = (getSelectedValue(form, cfg.metalFilterName) || '').trim().toLowerCase();

    let family = 'yellow';
    if (metalRaw.includes('rose')) family = 'rose';
    else if (metalRaw.includes('white')) family = 'white';
    else if (metalRaw.includes('silver')) family = 'silver';
    else if (metalRaw.includes('platinum')) family = 'platinum';
    else if (metalRaw.includes('palladium')) family = 'palladium';
    else if (metalRaw.includes('yellow')) family = 'yellow';

    const purityStep = getStepEl(root, cfg.purityFilterName);
    if (!purityStep) return;

    const fixed = { '925': '#e7e7e7', '500': '#b8b8b8', '950': '#e0e0e0' };
    const yellow = { '9ct': '#ead48a', '14ct': '#d8c06d', '18ct': '#c9aa52', '22ct': '#b8922d' };
    const white  = { '9ct': '#d9d9d9', '14ct': '#d9d9d9', '18ct': '#d9d9d9', '22ct': '#d9d9d9' };
    const rose   = { '9ct': '#d9a29a', '14ct': '#e2b2ab', '18ct': '#ebc3bd', '22ct': '#f1d0cb' };

    purityStep.querySelectorAll('[data-rc-option][data-purity-key]').forEach((opt) => {
      const pk = (opt.getAttribute('data-purity-key') || '').toLowerCase();
      const sw = opt.querySelector('.rc-pill__swatch');
      if (!sw) return;

      if (pk.includes('925')) return sw.style.setProperty('--rc-swatch', fixed['925']);
      if (pk.includes('500')) return sw.style.setProperty('--rc-swatch', fixed['500']);
      if (pk.includes('950')) return sw.style.setProperty('--rc-swatch', fixed['950']);

      let map = yellow;
      if (family === 'white') map = white;
      if (family === 'rose') map = rose;

      const key =
        pk.includes('22ct') ? '22ct' :
        pk.includes('18ct') ? '18ct' :
        pk.includes('14ct') ? '14ct' :
        pk.includes('9ct')  ? '9ct'  : null;

      if (key && map[key]) sw.style.setProperty('--rc-swatch', map[key]);
    });
  }

  function applyPurityRules(root, cfg) {
    if (!cfg.enablePurityRules) return;

    const form = root.querySelector('form');
    if (!form) return;

    const purityStep = getStepEl(root, cfg.purityFilterName);
    if (!purityStep) return;

    const metal = getSelectedValue(form, cfg.metalFilterName) || '';
    const rules = safeJsonParse(cfg.purityRules, {});
    const allowed = metal && rules ? rules[metal] : null;

    purityStep.querySelectorAll('[data-rc-option]').forEach((opt) => {
      const val = (opt.getAttribute('data-value') || '').trim();
      const shouldDisable = Array.isArray(allowed) ? !allowed.includes(val) : false;

      opt.classList.toggle('is-disabled', shouldDisable);

      const input = opt.querySelector('input[type="radio"]');
      if (input) {
        input.disabled = shouldDisable;
        if (shouldDisable && input.checked) input.checked = false;
      }
    });

    const currentPurity = getSelectedValue(form, cfg.purityFilterName);
    const enabledCandidate = firstEnabledRadio(purityStep);
    if (!currentPurity && enabledCandidate) enabledCandidate.checked = true;

    syncSelectedClasses(purityStep);
  }

  async function fetchLookupUrlFromCollectionHtml(collectionHandle, queryString, signal) {
    const base = `/collections/${collectionHandle}?section_id=${encodeURIComponent(COLLECTION_GRID_SECTION_ID)}`;
    const url = queryString ? `${base}&${queryString}` : base;

    const res = await fetch(url, { method: 'GET', signal });
    const html = await res.text();

    // Reliable original approach
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const a = doc.querySelector('a[href^="/products/"]');
    if (!a) return null;

    const href = a.getAttribute('href');
    return href ? href.split('?')[0] : null;
  }

  function collectSelections(root) {
    const form = root.querySelector('form');
    if (!form) return {};
    const selections = {};
    root.querySelectorAll('[data-rc-step]').forEach((step) => {
      const filterName = step.getAttribute('data-filter-name');
      if (!filterName) return;
      selections[filterName] = getSelectedValue(form, filterName);
    });
    return selections;
  }

  function getMissingRequired(root, selections) {
    const missing = [];
    root.querySelectorAll('[data-rc-step]').forEach((step) => {
      const filterName = step.getAttribute('data-filter-name');
      if (!filterName) return;

      const inputs = step.querySelectorAll('input[type="radio"]');
      const enabledInputs = Array.from(inputs).filter((i) => !i.disabled);
      if (enabledInputs.length === 0) return;

      if (!selections[filterName]) missing.push(filterName);
    });
    return missing;
  }

  async function doApply(root, cfg, state) {
    applyPurityRules(root, cfg);
    applyPuritySwatchesByMetal(root, cfg);
    syncAllSelectedClasses(root);

    const selections = collectSelections(root);
    const missing = getMissingRequired(root, selections);
    if (missing.length) {
      setLoading(root, false);
      setResult(root, `<span>Please select: ${missing.join(', ')}</span>`);
      return;
    }

    const mapObj = safeJsonParse(cfg.filterKeyMap, {});
    const queryString = buildFilterQueryString(mapObj, selections);

    if (!cfg.collectionHandle) {
      setLoading(root, false);
      setResult(root, `<span>Please set collection handle for lookup.</span>`);
      return;
    }

    if (state) state.lastQs = queryString;

    // Cache hit
    if (state && state.cache.has(queryString)) {
      const cachedUrl = state.cache.get(queryString);
      if (!cachedUrl) {
        setLoading(root, false);
        setResult(root, `<span>No match found.</span>`);
        return;
      }
      if (window.location.pathname === cachedUrl) {
        setLoading(root, false);
        setResult(root, ``);
        return;
      }

      setLoading(root, true);
      setResult(root, `<span>Updating ring…</span>`);
      // flush already done inside setLoading
      window.location.href = cachedUrl;
      return;
    }

    setLoading(root, true);
    setResult(root, `Finding matching ring…`);

    // Abort previous lookup if still running
    if (state && state.pendingController) {
      try { state.pendingController.abort(); } catch (e) {}
    }
    const controller = (state ? new AbortController() : null);
    if (state) state.pendingController = controller;

    try {
      const productUrl = await fetchLookupUrlFromCollectionHtml(
        cfg.collectionHandle,
        queryString,
        controller ? controller.signal : undefined
      );

      if (controller && controller.signal.aborted) return;

      if (state) state.cache.set(queryString, productUrl || null);

      if (!productUrl) {
        setLoading(root, false);
        setResult(root, `<span>No match found.</span>`);
        return;
      }

      if (window.location.pathname === productUrl) {
        setLoading(root, false);
        setResult(root, ``);
        return;
      }

      setResult(root, `<span>Updating ring…</span>`);
      // tiny extra flush before navigation
      root.offsetHeight;
      window.location.href = productUrl;
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      console.error(e);
      setLoading(root, false);
      setResult(root, `<span>Could not find the matching ring. Please try again.</span>`);
    } finally {
      if (state && state.pendingController === controller) {
        state.pendingController = null;
      }
      setLoading(root, false);
    }
  }

  // ----------------------------
  // MOUNTING (SIMPLE + ROBUST)
  // ----------------------------

  const MOUNT_ID = 'rc-pdp-mount';

  function getMountEl(sectionId) {
    if (sectionId) {
      const preferred = document.getElementById(`${MOUNT_ID}-${sectionId}`);
      if (preferred) return preferred;
    }
    return document.getElementById(MOUNT_ID);
  }

  function tryMount(root, sectionId) {
    const mount = getMountEl(sectionId);
    if (!mount) return false;
    if (mount.contains(root)) return true;
    mount.appendChild(root);
    return true;
  }

  function mountWithRetries(root, sectionId) {
    if (tryMount(root, sectionId)) return;

    let tries = 0;
    const maxTries = 60; // 6 seconds
    const timer = setInterval(() => {
      tries += 1;
      if (tryMount(root, sectionId) || tries >= maxTries) clearInterval(timer);
    }, 100);

    const obs = new MutationObserver(() => {
      if (tryMount(root, sectionId)) obs.disconnect();
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });

    window.addEventListener('load', () => tryMount(root, sectionId), { once: true });
  }

  // ACCORDION helpers
  function shouldDefaultOpen() {
    return window.matchMedia && window.matchMedia(`(min-width: ${ACC_BREAKPOINT}px)`).matches;
  }

  function initAccordion(root) {
    const acc = root.querySelector('[data-rc-acc]');
    if (!acc) return;

    const btn = acc.querySelector('[data-rc-acc-btn]');
    const panel = acc.querySelector('[data-rc-acc-panel]');
    if (!btn || !panel) return;

    // ALWAYS open for now
    const shouldOpen = true;

    acc.classList.toggle('is-open', shouldOpen);
    btn.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    panel.hidden = !shouldOpen;

    btn.addEventListener('click', () => {
      const isOpen = acc.classList.contains('is-open');
      acc.classList.toggle('is-open', !isOpen);
      btn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
      panel.hidden = isOpen;
    });
  }

  function initConfigurator(root) {
    const sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    if (root.dataset.rcInited === 'true') return;
    root.dataset.rcInited = 'true';

    const cfg = getSectionConfig(sectionId);
    if (!cfg) return;

    const state = {
      cache: new Map(),
      pendingController: null,
      lastQs: '',
      uiQueued: false
    };

    mountWithRetries(root, sectionId);
    initAccordion(root);

    const form = root.querySelector('form');
    if (!form) return;

    const applyBtn = root.querySelector('[data-rc-apply]');

    function queueUiSync() {
      if (state.uiQueued) return;
      state.uiQueued = true;
      requestAnimationFrame(() => {
        state.uiQueued = false;
        applyPurityRules(root, cfg);
        applyPuritySwatchesByMetal(root, cfg);
        syncAllSelectedClasses(root);
      });
    }

    const debouncedAutoApply = debounce(() => doApply(root, cfg, state), cfg.debounceMs || 200);

    form.addEventListener('change', () => {
      queueUiSync();
      if (cfg.autoRedirect) debouncedAutoApply();
    });

    if (applyBtn) applyBtn.addEventListener('click', () => doApply(root, cfg, state));

    queueUiSync();

    if (cfg.autoRedirect && applyBtn) {
      applyBtn.closest('div')?.classList.add('rc-hide-apply');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-rc-pdp]').forEach(initConfigurator);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-rc-pdp]');
    if (root) initConfigurator(root);
  });
})();